import * as XLSX from "xlsx";
import { requireSupabase } from "./supabase";
import type { PayrollAttendanceExport } from "./types";

export async function getPayrollAttendanceExport(startDate: string, endDate: string) {
  const { data, error } = await requireSupabase().rpc("payroll_export_attendance_period", {
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw error;
  return data as PayrollAttendanceExport;
}

function formatValue(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "ใช่" : "ไม่ใช่";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

function makeSheet(rows: Array<Record<string, unknown>>) {
  const keys = rows.length > 0 ? Object.keys(rows[0]) : [];
  const values = rows.map((row) => keys.map((key) => formatValue(row[key])));
  const sheet = XLSX.utils.aoa_to_sheet([keys, ...values]);
  sheet["!cols"] = keys.map((key) => ({ wch: Math.min(42, Math.max(14, key.length + 2)) }));
  return sheet;
}

export function downloadPayrollAttendanceWorkbook(report: PayrollAttendanceExport, fileName: string) {
  const workbook = XLSX.utils.book_new();
  const summary = report.summary.map((row) => ({
    "รหัสบุคลากร": row.personnelId,
    "ชื่อ": row.fullName,
    "แผนก": row.department,
    "ประเภท": row.personnelType,
    "วันที่": row.workDate,
    "เวลาเข้า (เซิร์ฟเวอร์)": row.checkInBangkok ?? "",
    "เวลาออก (เซิร์ฟเวอร์)": row.checkOutBangkok ?? "",
    "จำนวน Field Work": row.fieldWorkCount,
    "สถานะ": row.status,
    "ประเภทลา": row.leaveType ?? "",
    "สถานะการจ่าย": row.payStatus ?? "",
    "วันจ่าย": row.payableDay,
    "วันไม่จ่าย": row.unpaidDay,
    "ต้องตรวจสอบ": row.reviewRequired,
    "เหตุผล": row.reviewReason ?? "",
    "Event IDs": row.eventIds,
    "Leave Request ID": row.leaveRequestId ?? "",
    "Schedule Pair ID": row.schedulePairId ?? "",
  }));
  const events = report.events.map((row) => ({
    "Event ID": row.eventId,
    "รหัสบุคลากร": row.personnelId,
    "ชื่อ": row.fullName,
    "แผนก": row.department,
    "ประเภท": row.personnelType,
    "ประเภทเหตุการณ์": row.eventType,
    "Server Timestamp (UTC)": row.serverTimestamp,
    "Server Timestamp (Bangkok)": row.serverTimestampBangkok,
    "Client Timestamp": row.clientTimestamp ?? "",
    "Work Mode": row.workMode ?? "",
    "Work Type": row.workType ?? "",
    "Case ID": row.caseId ?? "",
    "สถานที่/ลูกค้า/โครงการ": row.customerProject ?? row.location ?? "",
    "อยู่ใน Geofence": row.withinGeofence,
    "ระยะห่าง (เมตร)": row.distanceMeters ?? "",
    "Exception": row.exceptionCode ?? "",
    "ต้องตรวจสอบ": row.needsReview,
    "เหตุผลนอกพื้นที่": row.outsideReason ?? "",
    "หมายเหตุ": row.note ?? "",
  }));
  const review = report.review.map((row) => ({
    "วันที่": row.workDate,
    "รหัสบุคลากร": row.personnelId,
    "ชื่อ": row.fullName,
    "ประเภท": row.personnelType,
    "สถานะ": row.status,
    "ประเภทลา": row.leaveType ?? "",
    "สถานะการจ่าย": row.payStatus ?? "",
    "เหตุผลที่ต้องตรวจ": row.reviewReason ?? "",
    "เวลาเข้า": row.checkInBangkok ?? "",
    "เวลาออก": row.checkOutBangkok ?? "",
    "Event IDs": row.eventIds,
    "Leave Request ID": row.leaveRequestId ?? "",
  }));
  const readme = [
    { รายการ: "ช่วงวันที่", ค่า: `${report.period.startDate} ถึง ${report.period.endDate}` },
    { รายการ: "Timezone", ค่า: report.period.timezone },
    { รายการ: "สถานะรอบ Payroll", ค่า: report.period.status },
    { รายการ: "ประเภทไฟล์", ค่า: "Draft / Review — ยังไม่ใช่ยอดจ่ายที่อนุมัติหรือล็อก" },
    { รายการ: "หลักเวลา", ค่า: "ใช้ server timestamp เป็นหลัก; client timestamp เป็นข้อมูลอ้างอิง" },
    { รายการ: "ข้อจำกัด", ค่า: "ไม่มีการตัดเงินอัตโนมัติจากการขาดงานหรือมาสาย" },
    { รายการ: "เวลาส่งออก", ค่า: report.period.exportedAt },
  ];

  XLSX.utils.book_append_sheet(workbook, makeSheet(summary), "Daily Summary");
  XLSX.utils.book_append_sheet(workbook, makeSheet(events), "Raw Events");
  XLSX.utils.book_append_sheet(workbook, makeSheet(review), "Review");
  XLSX.utils.book_append_sheet(workbook, makeSheet(readme), "Read Me");
  XLSX.writeFile(workbook, fileName, { bookType: "xlsx" });
}
